import { useMemo } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { geofenceService } from '../services/geofence.service';

// Minimal job interface - the hook accepts any object with these properties
interface JobLike {
  job_id: number;
  job_name: string;
  lat?: number | null;
  lng?: number | null;
  geofence_data?: {
    radius?: number;
  };
}

interface LocationMismatchResult<T> {
  hasMismatch: boolean;
  detectedJob: T | null;
  jobsAtLocation: T[];
  isAtClockedInJob: boolean;
  isLocationStale: boolean;
}

const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

// Buffer added to clocked-in job's radius to prevent GPS drift from triggering switch modal
// 1 mile = 1609 meters — must move significantly away before being considered "not at job"
const CLOCKED_IN_BUFFER_METERS = 1609;

// Shop job IDs - don't suggest switching to these locations
const SHOP_JOB_IDS = [1, 2];

export const useLocationMismatch = <T extends JobLike>(
  clockedInJobId: number | null,
  jobs: T[]
): LocationMismatchResult<T> => {
  const { location } = useLocation();

  return useMemo(() => {
    const isLocationStale = !location || (Date.now() - location.timestamp) > STALE_THRESHOLD;

    // Default result when we can't determine mismatch
    if (!location || clockedInJobId === null || jobs.length === 0) {
      return {
        hasMismatch: false,
        detectedJob: null,
        jobsAtLocation: [],
        isAtClockedInJob: false,
        isLocationStale
      };
    }

    // Geofence containment respecting geofence_type. Polygon jobs use
    // ray-casting against geofence_data.points ({lat,lng}); circle jobs use
    // distance vs radius. bufferM only applies to circles (a polygon has no
    // single center to expand) — used to soften GPS drift near the
    // clocked-in job; for polygons we test the true shape.
    const isInsideJobGeofence = (job: any, bufferM = 0): boolean => {
      if (!job?.lat || !job?.lng) return false;
      // Honor "geofence off" — disabled fences must never drive auto-switch
      // (neither to nor from). Otherwise the popup nominates jobs the admin
      // explicitly opted out of (e.g., Juan getting prompted into Arborwood).
      if (job.geofence_enabled === false) return false;
      if (job.geofence_type === 'polygon' && Array.isArray(job.geofence_data?.points) && job.geofence_data.points.length >= 3) {
        return geofenceService.isInsidePolygon(location.latitude, location.longitude, job.geofence_data.points);
      }
      const radius = (job.geofence_data?.radius || 100) + bufferM;
      return geofenceService.isInsideCircle(location.latitude, location.longitude, job.lat, job.lng, radius);
    };

    // Find ALL jobs the user is currently within the geofence of (circle OR polygon)
    const jobsAtLocation = jobs.filter(job => isInsideJobGeofence(job));

    // Check if user is still near the clocked-in job (circle gets a buffer to
    // absorb GPS drift; polygon tested exactly).
    const clockedInJob = jobs.find(j => j.job_id === clockedInJobId);

    // If the user is clocked into a job whose fence is disabled, suppress all
    // mismatch prompts. That job was selected manually; location can't sensibly
    // judge it, and we don't want to nudge them away from it.
    if (clockedInJob && (clockedInJob as any).geofence_enabled === false) {
      return { hasMismatch: false, detectedJob: null, jobsAtLocation: [], isAtClockedInJob: true, isLocationStale };
    }

    const isAtClockedInJob = clockedInJob
      ? isInsideJobGeofence(clockedInJob, CLOCKED_IN_BUFFER_METERS)
      : jobsAtLocation.some(j => j.job_id === clockedInJobId);

    // Filter out shop jobs from potential switch suggestions
    // We don't want to prompt users to switch TO a shop location
    const nonShopJobsAtLocation = jobsAtLocation.filter(j => !SHOP_JOB_IDS.includes(j.job_id));

    // Mismatch = at some non-shop job(s) but NOT the clocked-in job.
    // Ambiguity rule: when two or more job fences overlap at the user's spot we
    // can't pick the right one — don't auto-prompt. Forces a manual selection
    // instead of guessing wrong (which is worse than silence).
    const hasMismatch = !isAtClockedInJob && nonShopJobsAtLocation.length === 1;

    // Return the first non-shop detected job for the modal
    const detectedJob = hasMismatch ? nonShopJobsAtLocation[0] : null;

    return {
      hasMismatch,
      detectedJob,
      jobsAtLocation,
      isAtClockedInJob,
      isLocationStale
    };
  }, [location, clockedInJobId, jobs]);
};
